import {
  useTranslate,
  IResourceComponentsProps,
  useExport,
  HttpError,
  CrudFilters,
  useSelect,
  getDefaultFilter,
  useImport,
} from "@refinedev/core";

import { List, useTable } from "@refinedev/antd";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  FormProps,
  Form,
  Select,
  Input,
  InputRef,
} from "antd";

import {
  ICredential,
  ICredentialFilterVariables,
} from "interfaces";
import { CredentialActions } from "components/credentialActions";
import { useRef } from "react";

export const CredentialList: React.FC<IResourceComponentsProps> = () => {
  const {
    tableProps,
    filters,
    searchFormProps,
  } = useTable<ICredential, HttpError, ICredentialFilterVariables>({
    resource: "credentials",

    onSearch: (params) => {
      const filters: any = [];

      const { service } = params;

      filters.push({
        field: "service",
        operator: "eq",
        value: service,
      });

      return filters;
    },
  });

  const t = useTranslate();

  const { triggerExport, isLoading: isExportLoading } = useExport<ICredential>({
    resource: "credentials",
    mapData: (item) => item,
  });

  const { inputProps, isLoading: isImportLoading } = useImport<ICredential>({
    resource: "credentials",
    batchSize: 1,
  });

  const importInputRef = useRef<InputRef>(null!)

  return (
    <Row gutter={[16, 16]}>
      <Col
        xl={6}
        lg={24}
        xs={24}
        style={{
          marginTop: "52px",
        }}
      >
        <Card title={t("orders.filter.title")}>
          <Filter formProps={searchFormProps} filters={filters || []} />
        </Card>
      </Col>{" "}
      <Col xl={18} xs={24}>
        <List>
          <div style={{ display: "flex", gap: 4 }}>
            <Button
              type="primary"
              disabled={isImportLoading}
              onClick={() => importInputRef.current.input?.click()}
            >
              {isImportLoading ? "Importing..." : "Import from CSV"}
            </Button>

            <Button
              type="primary"
              disabled={isExportLoading}
              onClick={triggerExport}
            >
              {isExportLoading ? "Exporting..." : "Export as CSV"}
            </Button>

            <Input
              name="csv"
              hidden
              ref={importInputRef}
              {...inputProps}
            />
          </div>

          <Table {...tableProps} rowKey="id">
            <Table.Column<ICredential>
              key="service"
              dataIndex={"service"}
              title={t("Service")}
              render={(value) => value}
            />
            <Table.Column<ICredential>
              key="description"
              dataIndex={"description"}
              title={t("Description")}
              render={(value) => value}
            />
            <Table.Column<ICredential>
              key="url"
              dataIndex={"url"}
              title={t("URL")}
              render={(value) => <a href={value} target="_blank">{value}</a>}
            />
            <Table.Column<ICredential>
              key="username"
              dataIndex={"username"}
              title={t("Username")}
              render={(value) => value}
            />
            <Table.Column<ICredential>
              key="password"
              dataIndex={"password"}
              title={t("Password")}
              render={(value) => value}
            />
            <Table.Column<ICredential>
              key="renewalDate"
              dataIndex={"renewalDate"}
              title={t("Renewal Date")}
              render={(value) => value}
            />
            <Table.Column<ICredential>
              key="billingInfo"
              dataIndex={"billingInfo"}
              title={t("Billing Info")}
              render={(value) => value}
            />
            <Table.Column<ICredential>
              fixed="right"
              title={t("table.actions")}
              dataIndex="actions"
              key="actions"
              align="center"
              render={(_value, record) => <CredentialActions record={record} />}
            />
          </Table>
        </List>
      </Col>
    </Row>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const t = useTranslate();
  const { formProps, filters } = props;
  const credentialsServiceProps = useSelect<ICredential>({
    resource: "credentials",
    optionLabel: "service" as any,
    optionValue: "service" as any,
    defaultValue: getDefaultFilter("credential.service", filters),

    pagination: {
      mode: "server",
    },
  });

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        status: getDefaultFilter("credential.service", filters),
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item label={"Service"} name="service">
            <Select
              options={credentialsServiceProps.options}
              allowClear
              placeholder={"Search services"}
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item>
            <Button htmlType="submit" type="primary" size="large" block>
              {t("orders.filter.submit")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
